import React from "react";
import { Link } from "react-router-dom";

import "../../components/panels/Panels.scss";
import {
    Alignment,
    FlexDirection,
    Side,
    Size,
} from "../../types/system/Layout";
import Utilities from "../../foundation/Utility";
import Button from "../buttons/Button";
import ButtonThemes from "../../types/enums/ButtonThemes";

interface PanelProps {
    children?: any;
    className?: string;

    title?: string;
    subtitle?: string;
    icon?: string;
    iconFont?: string;
    iconFontColor?: string;
    moreLink?: string;
    moreLinkText?: string;
    moreLinkNew?: boolean;
    moreLinkExternal?: boolean;
    moreButtonText?: string;
    moreButtonOnClick?: Function;
    loading?: boolean;
    double?: boolean;
    uniform?: boolean;

    size?: string;
    width?: number;
    height?: string;
    minHeight?: string;
    maxHeight?: string;

    flex?: FlexDirection;
    flexSpaced?: boolean;
    flexWrapped?: boolean;
    overflow?: Side;
    alignment?: Alignment;

    squaredSide?: Side;

    clearanceSide?: Side;
    clearanceSize?: Size;

    bufferSide?: Side;
    bufferSize?: Size;

    containContent?: boolean;
    chartContent?: boolean;
    gridContent?: boolean;
    center?: boolean;
    bordered?: boolean;
    style?: React.CSSProperties;

    contextKey?: string;

    secureAdd?: boolean;
    secureReset?: boolean;
    secureSave?: boolean;
    addDisabled?: boolean;
    addTooltip?: string;
    resetDisabled?: boolean;
    resetTooltip?: string;
    saveDisabled?: boolean;
    saveTooltip?: string;

    onAdd?: () => void;
    onReset?: () => void;
    onSave?: () => void;
}

class Panel extends React.Component<PanelProps> {
    render(): React.ReactNode {
        let classNames = this.props.className ? ` ${this.props.className}` : "";

        classNames += this.props.containContent
            ? " scroll-container"
            : this.props.chartContent
            ? " static-container"
            : this.props.gridContent
            ? " grid-container"
            : " container";

        if (this.props.size) classNames += ` ${this.props.size}`;
        if (this.props.center) classNames += " center";
        if (this.props.double) classNames += " double";
        if (this.props.containContent) classNames += " contain";
        if (this.props.bordered) classNames += " bordered";
        if (this.props.uniform) classNames += " uniform";

        // Clearance classes
        if (
            !!this.props.clearanceSide &&
            this.props.clearanceSide !== Side.None
        ) {
            classNames += ` ${Utilities.getSideClass(
                this.props.clearanceSide,
            )}clearance${Utilities.getSizeClass(this.props.clearanceSize)}`;
        }

        const bufferSide = this.props.bufferSide ?? Side.All;
        // Buffer classes
        if (bufferSide !== Side.None) {
            classNames += ` ${Utilities.getSideClass(
                bufferSide,
            )}buffer${Utilities.getSizeClass(this.props.bufferSize)}`;
        }

        // Squared classes
        if (!!this.props.squaredSide) {
            classNames += ` ${Utilities.getSideClass(
                this.props.squaredSide,
            )}squared`;
        }

        switch (this.props.flex) {
            case "row":
                classNames += " flex-row";
                break;
            case "column":
                classNames += " flex-column";
                break;
            case "reverse-row":
                classNames += " flex-row-reverse";
                break;
            case "reverse-column":
                classNames += " flex-column-reverse";
                break;
        }

        if (this.props.flexSpaced) classNames += " spaced";

        if (this.props.flexWrapped) classNames += " flex-wrapped";

        // Overflow classes
        if (this.props.overflow) {
            switch (this.props.overflow) {
                case "vertical":
                    classNames += " overflow-vertical";
                    break;
                case "horizontal":
                    classNames += " overflow-horizontal";
                    break;
                case "all":
                    classNames += " overflow-all";
                    break;
            }
        }

        // Alignment classes
        if (this.props.alignment) {
            switch (this.props.alignment) {
                case "left":
                    classNames += " left";
                    break;
                case "center":
                    classNames += " center";
                    break;
                case "right":
                    classNames += " right";
                    break;
            }
        }

        return (
            <div
                className={`panel${classNames}`}
                style={{
                    width: this.props.width,
                    minHeight: this.props.minHeight,
                    maxHeight: this.props.maxHeight,
                    height: this.props.height,
                    ...this.props.style,
                }}
                data-contextkey={this.props.contextKey}>
                {(this.props.title || this.props.subtitle) && (
                    <div className="header">
                        {this.props.icon && (
                            <img
                                src={this.props.icon}
                                alt="Icon"></img>
                        )}
                        {this.props.iconFont && (
                            <i
                                className={`fa-solid ${this.props.iconFontColor} fa-${this.props.iconFont}`}></i>
                        )}
                        {this.props.title && (
                            <div className="title">{this.props.title}</div>
                        )}
                        {this.props.subtitle && (
                            <div className="subtitle">
                                {this.props.subtitle}
                            </div>
                        )}
                        {this.props.onAdd && (
                            <Button
                                icon="add"
                                inline={true}
                                secure={this.props.secureAdd}
                                onClick={this.props.onAdd}
                                disabled={this.props.addDisabled}
                                tooltip={this.props.addTooltip}
                                tooltipPlace="bottom"
                            />
                        )}
                        {this.props.onReset && (
                            <Button
                                icon="history"
                                inline={true}
                                secure={this.props.secureReset}
                                onClick={this.props.onReset}
                                disabled={this.props.resetDisabled}
                                tooltip={this.props.resetTooltip}
                                tooltipPlace="bottom"
                            />
                        )}
                        {this.props.onSave && (
                            <Button
                                icon="save"
                                inline={true}
                                secure={this.props.secureSave}
                                theme={ButtonThemes.Theme}
                                onClick={this.props.onSave}
                                disabled={this.props.saveDisabled}
                                tooltip={this.props.saveTooltip}
                                tooltipPlace="bottom"
                            />
                        )}
                    </div>
                )}
                {this.props.loading ? (
                    <div className="loading">
                        <div className="loader"></div>
                    </div>
                ) : (
                    this.props.children
                )}

                {this.props.moreLink && (
                    <div className="more-link">
                        {this.props.moreLinkExternal ? (
                            <a
                                target="_blank"
                                href={this.props.moreLink}
                                rel="noreferrer">
                                {this.props.moreLinkText ?? "View Now »"}
                            </a>
                        ) : (
                            <Link
                                target={
                                    this.props.moreLinkNew ? "_blank" : "_self"
                                }
                                to={this.props.moreLink}>
                                {this.props.moreLinkText ?? "View Now »"}
                            </Link>
                        )}
                    </div>
                )}
                {this.props.moreButtonText && (
                    <div className="more-link">
                        <button
                            className="no-image theme-button bold"
                            onClickCapture={() =>
                                this.props.moreButtonOnClick()
                            }>
                            {this.props.moreButtonText}
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

export default Panel;
